import * as plugins from '../../00plugins.js';
import { zIndexLayers } from '../../00zindex.js';
import { DeesWindowLayer } from '../../00group-overlay/dees-windowlayer/dees-windowlayer.js';

import {
  DeesElement,
  type TemplateResult,
  property,
  customElement,
  html,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';

@customElement('dees-appui-profiledropdown')
export class DeesAppuiProfileDropdown extends DeesElement {
  public static demo = () => html`
    <dees-appui-profiledropdown
      .user=${{
        name: 'John Doe',
        email: 'john.doe@example.com',
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
        status: 'online' as 'online'
      }}
      .menuItems=${[
        { name: 'Profile Settings', iconName: 'user', action: async () => console.log('Profile') },
        { name: 'Account', iconName: 'settings', action: async () => console.log('Account') },
        { divider: true },
        { name: 'Help & Support', iconName: 'helpCircle', action: async () => console.log('Help') },
        { name: 'Keyboard Shortcuts', iconName: 'keyboard', shortcut: 'Cmd+K', action: async () => console.log('Shortcuts') },
        { divider: true },
        { name: 'Sign Out', iconName: 'logOut', action: async () => console.log('Sign out') }
      ]}
      .isOpen=${true}
    ></dees-appui-profiledropdown>
  `;
  public static demoGroups = ['App UI'];

  /**
   * Open the profile dropdown as a body-level overlay anchored under `triggerEl`.
   *
   * The appbar establishes a stacking context via `backdrop-filter` and clips its
   * `.account` region with `overflow: hidden`, so an inline dropdown is both
   * trapped below page content and cut off. Portaling to `document.body` behind a
   * transparent window layer — the same path the appbar's submenus use — escapes
   * both, and lets the dropdown's own vibrancy sample the page content behind it.
   */
  public static async openFromTrigger(
    triggerEl: HTMLElement,
    optionsArg: {
      user?: DeesAppuiProfileDropdown['user'];
      menuItems?: DeesAppuiProfileDropdown['menuItems'];
      onSelect?: (detail: unknown) => void;
    },
  ): Promise<DeesAppuiProfileDropdown> {
    const dropdown = new DeesAppuiProfileDropdown();
    dropdown.user = optionsArg.user;
    dropdown.menuItems = optionsArg.menuItems ?? [];
    dropdown.setAttribute('portaled', '');
    dropdown.isOpen = true;
    dropdown.style.position = 'fixed';
    dropdown.style.zIndex = String(zIndexLayers.overlay.dropdown);
    dropdown.style.visibility = 'hidden';

    // Transparent click-catcher behind the dropdown (menus don't dim the page).
    // dismissOnOutsidePress closes on any press outside the dropdown or its
    // trigger — robust even when the appbar paints above the layer.
    const windowLayer = await DeesWindowLayer.createAndShow({ dimmed: false, dismissOnOutsidePress: true });
    windowLayer.protectedElements = [dropdown, triggerEl];
    dropdown.portalWindowLayer = windowLayer;
    windowLayer.addEventListener('click', () => {
      dropdown.closePortal();
    });

    dropdown.addEventListener('menu-select', (event: Event) => {
      optionsArg.onSelect?.((event as CustomEvent).detail);
      dropdown.closePortal();
    });

    document.body.append(dropdown);
    await dropdown.updateComplete;
    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

    // Anchor below the trigger, right-aligned, clamped to the viewport.
    const triggerRect = triggerEl.getBoundingClientRect();
    const rect = dropdown.getBoundingClientRect();
    const margin = 8;
    let left = triggerRect.right - rect.width;
    let top = triggerRect.bottom + 4;
    if (left + rect.width > window.innerWidth - margin) {
      left = window.innerWidth - rect.width - margin;
    }
    if (left < margin) {
      left = margin;
    }
    if (top + rect.height > window.innerHeight - margin) {
      // Not enough room below — flip above the trigger.
      top = triggerRect.top - rect.height - 4;
      if (top < margin) {
        top = margin;
      }
    }
    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
    dropdown.style.visibility = 'visible';

    return dropdown;
  }

  /** Set when opened via openFromTrigger(); the backing transparent click layer.
   *  Outside-press dismissal is handled centrally by the layer's
   *  `dismissOnOutsidePress` (the dropdown + its trigger are registered as the
   *  layer's `protectedElements`). */
  private portalWindowLayer?: DeesWindowLayer;

  @property({ type: Object })
  accessor user: {
    name: string;
    email?: string;
    avatar?: string;
    status?: 'online' | 'offline' | 'busy' | 'away';
  } | undefined = undefined;

  @property({ type: Array })
  accessor menuItems: (plugins.tsclass.website.IMenuItem & { shortcut?: string } | { divider: true })[] = [];

  @property({ type: Boolean, reflect: true })
  accessor isOpen: boolean = false;

  @property({ type: String })
  accessor position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' = 'top-right';

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        pointer-events: none;
      }

      .dropdown {
        position: absolute;
        min-width: 220px;
        background: var(--dees-material-regular-bg);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        box-shadow: var(--dees-shadow-lg);
        z-index: ${zIndexLayers.overlay.dropdown};
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), transform var(--dees-transition-default) var(--dees-ease-standard);
        pointer-events: none;
        overflow: hidden;
        font-size: var(--dees-font-caption1-size);
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .dropdown {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        .dropdown {
          background: var(--dees-material-regular-bg-opaque);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      :host([isopen]) .dropdown {
        opacity: 1;
        transform: scale(1) translateY(0);
        pointer-events: auto;
      }
      
      .backdrop {
        display: none;
      }

      /* Position variants */
      .dropdown.top-right {
        top: 100%;
        right: 0;
        margin-top: 4px;
      }

      .dropdown.top-left {
        top: 100%;
        left: 0;
        margin-top: 8px;
      }

      .dropdown.bottom-right {
        bottom: 100%;
        right: 0;
        margin-bottom: 8px;
      }

      .dropdown.bottom-left {
        bottom: 100%;
        left: 0;
        margin-bottom: 8px;
      }

      /* User section */
      .user-section {
        padding: 12px;
        border-bottom: 1px solid var(--dees-color-border-default);
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .user-avatar {
        position: relative;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--dees-color-code-block-bg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
        color: var(--dees-color-text-muted);
        overflow: hidden;
      }

      .user-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .user-status {
        position: absolute;
        bottom: 0;
        right: 0;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 2px solid var(--dees-color-bg-primary);
      }

      .user-status.online {
        background: var(--dees-color-accent-success);
      }

      .user-status.offline {
        background: var(--dees-color-text-muted);
      }

      .user-status.busy {
        background: var(--dees-color-accent-error);
      }

      .user-status.away {
        background: var(--dees-color-accent-warning);
      }

      .user-details {
        flex: 1;
        min-width: 0;
      }

      .user-name {
        font-size: 13px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        line-height: 1.2;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-email {
        font-size: 11px;
        color: var(--dees-color-text-secondary);
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Menu section */
      .menu-section {
        padding: 4px 0;
      }

      .menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        cursor: default;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm);
        line-height: 1;
        user-select: none;
      }

      .menu-item:hover {
        background: var(--dees-color-active);
      }

      .menu-item:active {
        background: var(--dees-color-pressed);
      }

      .menu-item dees-icon {
        font-size: 14px;
        color: var(--dees-color-text-secondary);
      }

      .menu-item-text {
        flex: 1;
      }

      .menu-shortcut {
        font-size: 11px;
        color: var(--dees-color-text-secondary);
        margin-left: auto;
      }

      .menu-divider {
        height: 1px;
        background: var(--dees-color-border-default);
        margin: 4px 0;
      }

      /* Backdrop for mobile */
      @media (max-width: 768px) {
        .backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: transparent;
          z-index: ${zIndexLayers.backdrop.dropdown};
          opacity: 0;
          transition: opacity var(--dees-transition-default) var(--dees-ease-standard);
          display: none;
        }

        :host([isopen]) .backdrop {
          display: block;
          opacity: 1;
          pointer-events: auto;
        }

        .dropdown {
          position: fixed;
          top: 50%;
          left: 50%;
          right: auto;
          bottom: auto;
          transform: translate(-50%, -50%) scale(0.95);
          margin: 0;
          max-width: calc(100vw - 32px);
          max-height: calc(100vh - 32px);
          overflow-y: auto;
          overscroll-behavior: contain;
        }

        :host([isopen]) .dropdown {
          transform: translate(-50%, -50%) scale(1);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .dropdown,
        .backdrop,
        .menu-item {
          transition: none;
        }
      }

      /* Portaled mode — the dropdown is rendered at document.body inside a
         transparent window layer and anchored under the trigger by
         openFromTrigger(). This escapes the appbar's backdrop-filter stacking
         context and its .account overflow:hidden clip, and lets the dropdown's
         own vibrancy sample the page content behind it. It appears directly
         (no transform/opacity animation, which would momentarily drop the
         backdrop-filter — same reasoning as dees-contextmenu). Placed last so
         it overrides the position variants and the mobile centering above. */
      :host([portaled]) {
        position: fixed;
        inset: auto;
        pointer-events: auto;
      }

      :host([portaled]) .dropdown {
        position: static;
        inset: auto;
        margin: 0;
        opacity: 1;
        transform: none;
        transition: none;
        pointer-events: auto;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="backdrop" @click=${() => this.close()}></div>
      <div class="dropdown ${this.position}">
        ${this.user ? html`
          <div class="user-section">
            <div class="user-info">
              <div class="user-avatar">
                ${this.user.avatar 
                  ? html`<img src="${this.user.avatar}" alt="${this.user.name}">`
                  : this.getInitials(this.user.name)
                }
                ${this.user.status ? html`
                  <div class="user-status ${this.user.status}"></div>
                ` : ''}
              </div>
              <div class="user-details">
                <div class="user-name">${this.user.name}</div>
                ${this.user.email ? html`
                  <div class="user-email">${this.user.email}</div>
                ` : ''}
              </div>
            </div>
          </div>
        ` : ''}
        
        <div class="menu-section">
          ${this.menuItems.map(item => this.renderMenuItem(item))}
        </div>
      </div>
    `;
  }

  private renderMenuItem(item: plugins.tsclass.website.IMenuItem & { shortcut?: string } | { divider: true }): TemplateResult {
    if ('divider' in item && item.divider) {
      return html`<div class="menu-divider"></div>`;
    }

    const menuItem = item as plugins.tsclass.website.IMenuItem & { iconName?: string; shortcut?: string };
    return html`
      <div class="menu-item" @click=${() => this.handleMenuClick(menuItem)}>
        ${menuItem.iconName ? html`
          <dees-icon .icon="${`lucide:${menuItem.iconName}`}"></dees-icon>
        ` : ''}
        <span class="menu-item-text">${menuItem.name}</span>
        ${menuItem.shortcut ? html`
          <span class="menu-shortcut">${menuItem.shortcut}</span>
        ` : ''}
      </div>
    `;
  }

  private getInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  private async handleMenuClick(item: plugins.tsclass.website.IMenuItem & { iconName?: string; shortcut?: string }) {
    await item.action();
    this.close();
    
    // Emit menu-select event
    this.dispatchEvent(new CustomEvent('menu-select', {
      detail: { item },
      bubbles: true,
      composed: true
    }));
  }

  public open() {
    this.isOpen = true;
  }

  public close() {
    this.isOpen = false;
  }

  public toggle() {
    this.isOpen = !this.isOpen;
  }

  /**
   * Tear down a portaled instance: destroy the backing window layer, remove the
   * element, and emit `portal-closed` so the opener can drop its reference.
   * Guarded so the layer click and a menu selection can't double-close.
   */
  public async closePortal(): Promise<void> {
    if (!this.portalWindowLayer) {
      return;
    }
    this.portalWindowLayer.destroy();
    this.portalWindowLayer = undefined;
    this.isOpen = false;
    this.dispatchEvent(new CustomEvent('portal-closed', { bubbles: true, composed: true }));
    this.remove();
  }

  // Handle clicks outside the dropdown
  async connectedCallback() {
    await super.connectedCallback();
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  private handleOutsideClick(event: MouseEvent) {
    if (this.isOpen && !this.contains(event.target as Node)) {
      // Check if the click is on the parent element (which contains the profile button)
      const parentElement = this.parentElement;
      if (parentElement && parentElement.contains(event.target as Node)) {
        // Don't close if clicking within the parent element (e.g., on the profile button)
        return;
      }
      this.close();
    }
  }
}
